import { Goal, MetricEntry, MetricEntryMode, Task } from '@griply/types';
import { startOfDay } from 'date-fns';

import { formatDateKey } from './format-date-key';
import { isCountableGoalTask } from './task-predicates';

export type ComputeGoalTaskProgressOptions = {
  goal: Goal;
  allTasks: Task[];
};

export type ComputeGoalTaskProgressResult = {
  entries: MetricEntry[];
  target: number;
  completionPercentage: number;
};

export const computeGoalTaskProgress = ({
  goal,
  allTasks,
}: ComputeGoalTaskProgressOptions): ComputeGoalTaskProgressResult => {
  const countableTasks = allTasks.filter(isCountableGoalTask);
  const target = countableTasks.length;

  const completedTasks = countableTasks
    .filter(({ completedAt }) => !!completedAt)
    .sort(
      (taskA, taskB) =>
        taskA.completedAt!.getTime() - taskB.completedAt!.getTime(),
    );

  const completionPercentage =
    Math.floor((completedTasks.length / target) * 100) || 0;

  const dateTasksMap = completedTasks.reduce<Record<string, MetricEntry>>(
    (acc, { completedAt }, currentIndex) => {
      const dateKey = formatDateKey(completedAt!);
      acc[dateKey] = acc[dateKey] ?? {
        id: dateKey,
        date: startOfDay(completedAt!),
        value: 0,
        mode: MetricEntryMode.Absolute,
      };
      acc[dateKey].value = currentIndex + 1;

      return acc;
    },
    {},
  );

  const goalStartDate = startOfDay(goal.startDate ?? goal.createdAt);

  const entries: MetricEntry[] = [
    {
      id: formatDateKey(goalStartDate),
      date: goalStartDate,
      value: 0,
      mode: MetricEntryMode.Absolute,
    },
    ...Object.values(dateTasksMap),
  ];

  return { entries, target, completionPercentage };
};
